import { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { s } from '@/theme/tokens';
import { Box, Button, Panel, Stack, Text } from '@/ui';
import { useBreadcrumbs } from '@/ui/layout/breadcrumbsContext';
import { Section } from '@/ui/layout/Section';
import { PageShell } from '@/ui/primitives/PageShell';
import type { ReactElement } from 'react';

const fallbackContentTextSx = { fontSize: '15px' };

type NotFoundScreenProps = {
  title?: string;
  body?: string;
  backTo?: string;
  backLabel?: string;
};

export const NotFoundScreen = ({
  title = 'Page Not Found',
  body = 'The page you are looking for does not exist. Check the URL or head back.',
  backTo = '/',
  backLabel = 'Go Back',
}: NotFoundScreenProps): ReactElement => {
  const { setOverrideVisible, clearOverride } = useBreadcrumbs();

  useEffect(() => {
    setOverrideVisible(false);
    return () => {
      clearOverride();
    };
  }, [clearOverride, setOverrideVisible]);

  return (
    <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="xl" sx={{ pt: s('5xl') }}>
        <Section>
          <Box sx={{ width: '100%' }}>
            <Panel
              title={title}
              actions={
                <Button component={RouterLink} to={backTo} variant="primary">
                  {backLabel}
                </Button>
              }
            >
              <Text role="bodyMuted" sx={fallbackContentTextSx}>
                {body}
              </Text>
            </Panel>
          </Box>
        </Section>
      </Stack>
    </PageShell>
  );
};
